import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Input, Typography, Tag } from 'antd';

import {
  activateDeactivateUserRequest,
  createUserRequest,
  getAllUsersRequest,
  updateUserRequest
} from '../../UserApiActions';
import AddUser from '../AddUser/AddUser';
import EditUserModal from '../EditUser/EditUser';
import { setPageTitle } from "../../../Dashboard/DashboardActions";
import UtilHelper from "../../../../utils/UtilHelper";
import GenaLoader from "../../../../utils/components/GenaLoader/GenaLoader";
import DataGrid from '../../../../utils/components/DataGrid/DataGrid';
import CustomIcon from '../../../../utils/components/CustomIcon/CustomIcon';
import Notification from '../../../../utils/Notification';

import './UserTable.css';

const notification = new Notification()

const UserTableComponent = (props) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const dataGridRef = useRef()

  useEffect(() => {
    setLoading(true)
    props.getAllUsers().then(() => {
    }).catch((error) => {
      setError(error)
    }).finally(() => {
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (!dataGridRef?.current?.api) return
    const searchText = debouncedSearch.trim()
    dataGridRef.current.api.setQuickFilter(searchText);
  }, [dataGridRef, debouncedSearch])

  // Debounce effect on search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    // Cancel timeout if search text changes before 600ms
    return () => clearTimeout(handler);
  }, [search]);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Name',
        maxWidth: 200,
        valueGetter: (params) => {
          const first = params.data.firstName || '';
          const last = params.data.lastName || '';
          return `${first} ${last}`.trim();
        }
      },
      {
        headerName: 'Email',
        field: 'email',
        maxWidth: 200,
      },
      {
        headerName: 'Location',
        field: 'location',
        autoHeight: true
      },
      ...(props.currentRole.isSuperAdmin
        ? [{
          headerName: 'Organization',
          field: 'Organization.name',
          autoHeight: true
        }]
        : []),
      {
        headerName: 'Cases',
        field: 'caseCount',
      },
      {
        headerName: 'Requests',
        field: 'searchRecordCount',
      },
      {
        headerName: 'Active',
        field: 'lastLoginAt',
        valueGetter: (params) => UtilHelper.getReadableDuration(params.data.lastLoginAt) || '-',
        cellRenderer: (params) => {
          return params.data.enabled === false ? (
            <Tag color="error">Account Deactivated</Tag>
          ) : (
            UtilHelper.getReadableDuration(params.data.lastLoginAt) || '-'
          );
        },
      },
      {
        headerName: 'Created',
        field: 'createdAt',
        getQuickFilterText: (params) => moment(params.data.createdAt).local().format('DD/MM/YYYY'),
        cellRenderer: (params) => moment(params.data.createdAt).local().format('DD/MM/YYYY')
      },
    ];
  }, [props.currentRole])

  const handleAddUser = async (user) => {
    await props.createUser(user)
  };

  const handleRowClick = (userObj) => {
    setSelectedUser(userObj)
    setShowEditUserModal(true)
  }

  const exportCSV = () => {
    dataGridRef?.current?.api.exportDataAsCsv({
      fileName: `users-${moment().format('DD-MM-YYYY')}.csv`,
    });
    notification.show('info', 'Data Exported');
  }

  return (
    <div className="flex-vertical-gap-4">
      <div style={{ display: 'flex', justifyContent: "space-between" }}>
        <Typography.Title level={2} style={{ margin: 0 }}>Users</Typography.Title>
        <div className="flex-horizontal-gap-2">
          <Button onClick={exportCSV}>Export<CustomIcon name="Download" /></Button>
          <AddUser handleAddUser={handleAddUser} />
        </div>
      </div>
      <Input
        allowClear
        placeholder="Search"
        prefix={<CustomIcon name="Search" />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <DataGrid
        gridRef={dataGridRef}
        loading={loading}
        error={error}
        columnDefs={columnDefs}
        rowData={props.users}
        onRowClicked={(event) => {
          handleRowClick(event.data)
        }}
        gridOptions={{
          quickFilterText: debouncedSearch
        }}
      />
      <EditUserModal visible={showEditUserModal} user={selectedUser} onCancel={() => {
        setShowEditUserModal(false)
        setSelectedUser(null)
      }} />
      {/* <GenaLoader showLoader={loading} /> */}
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    users: state.user.users,
    loggedInUser: state.auth.loggedInUser,
    currentRole: state.auth.currentRole
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllUsers: (id) => {
      return dispatch(getAllUsersRequest(id)).then((res) => {
        return Promise.resolve(res);
      }, (error) => {
        return Promise.reject(error);
      });
    },

    createUser: (data) => {
      return dispatch(createUserRequest(data)).then((res) => {
        return Promise.resolve(res);
      }, (error) => {
        return Promise.reject(error);
      });
    },

    updateUser: (userId, data) => {
      return dispatch(updateUserRequest(userId, data)).then((res) => {
        return Promise.resolve(res);
      }, (error) => {
        return Promise.reject(error);
      });
    },

    activateDeactivateUser: (userId, status) => {
      return dispatch(activateDeactivateUserRequest(userId, status)).then((res) => {
        return Promise.resolve(res);
      }, (error) => {
        return Promise.reject(error);
      });
    },

    updatePageTitle: (pageTitle, enablePageTitle) => {
      dispatch(setPageTitle(pageTitle, enablePageTitle));
    }
  }
};

const UserTable = connect(
  mapStateToProps,
  mapDispatchToProps
)(UserTableComponent);

export default UserTable;